import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import {
  getSubscriptionPlansApi,
  getSubscriptionMakersApi,
  getSubscriptionCarnamesApi,
  postSubscriptionApplyApi,
  getSubscriptionStatusApi,
  getSubscriptionVehicleStatusesApi,
} from '@/api/subscription';
import { getValidWarranties } from '@/components/pages/WarrantyManagement/WarrantyManagementHelper';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { SelectedTenant } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { Car, Warranty } from '@/types/api/customerApi';
import {
  SubscriptionInheritanceStatus,
  SubscriptionPlan,
} from '@/types/api/subscription';

type CarSelectMode = 'registered' | 'manual';

interface SubscriptionConfirmPageProps {
  initialCarSelectMode?: CarSelectMode;
  inheritanceStatus?: SubscriptionInheritanceStatus;
  preSelectedCarId?: number;
}

interface Maker {
  mk_cd: number;
  mk_nm: string;
}
interface CarName {
  carnm_cd: number;
  car_nm: string;
}

export const SubscriptionConfirmPage: FC<SubscriptionConfirmPageProps> = ({
  initialCarSelectMode,
  inheritanceStatus,
  preSelectedCarId,
}) => {
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [planError, setPlanError] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const authInfo = store.state.authInfo as any;
  const ownedCars = useStore(store, 'carsInfo') as Car[];
  const selectedTenant = useStore(store, 'getSelectedTenant') as SelectedTenant;
  const { warranties } = store.state as { warranties: Warranty[] };
  const validWarranties = getValidWarranties(warranties);
  const warrantyCarIds = new Set(
    validWarranties.map((w: any) => w.t_stock_car_id).filter(Boolean),
  );
  const allCars: Car[] = (ownedCars || []).filter(
    (car) => !warrantyCarIds.has(car.t_stock_car_id),
  );
  const isInheritanceApply = !!inheritanceStatus?.eligible;
  const inheritanceCar =
    allCars.find(
      (car) => car.t_stock_car_id === inheritanceStatus?.t_stock_car_id,
    ) || null;
  // 車両選択画面から明示的に選択された車両のみプリセレクト
  // ホーム画面経由の場合は preSelectedCarId が未指定のため車両未選択になる
  const initialCar =
    inheritanceCar ||
    (preSelectedCarId
      ? allCars.find((car) => car.t_stock_car_id === preSelectedCarId) || null
      : null);

  // 車両選択モード
  const [carSelectMode, setCarSelectMode] = useState<CarSelectMode>(
    isInheritanceApply
      ? 'registered'
      : initialCarSelectMode || (allCars.length > 0 ? 'registered' : 'manual'),
  );
  const [selectedCar, setSelectedCar] = useState<Car | null>(initialCar);

  // メーカー/車種選択
  const [makers, setMakers] = useState<Maker[]>([]);
  const [carnames, setCarnames] = useState<CarName[]>([]);
  const [selectedMkCd, setSelectedMkCd] = useState<number | null>(null);
  const [selectedCarnmCd, setSelectedCarnmCd] = useState<number | null>(null);
  const [selectedMakerName, setSelectedMakerName] = useState('');
  const [selectedCarName, setSelectedCarName] = useState('');

  useEffect(() => {
    if (isInheritanceApply && inheritanceCar) {
      setCarSelectMode('registered');
      setSelectedCar(inheritanceCar);
    }
  }, [inheritanceCar, isInheritanceApply]);

  useEffect(() => {
    let unmounted = false;

    // F7ルーター遷移アニメーション中は reloadCurrent が無視されるため
    // ページ遷移完了を待ってからナビゲーションする
    // ※ F7 v7 では router.animating が遷移初期に undefined になる場合があるため
    //   pageAfterIn イベントで確実に遷移完了を検知する
    const navigateWhenReady = (path: string, opts: Record<string, unknown>) => {
      const doNav = () => {
        if (unmounted) return;
        f7.view.main.router.navigate(path, opts);
      };
      // router.animating が true → アニメーション完了を待つ
      // router.animating が undefined/false → ページ遷移が完了しているか不明
      //   → pageAfterIn イベントで確実に待つ
      if ((f7.view.main.router as any).animating === false) {
        doNav();
      } else {
        const onPageIn = () => {
          f7.off('pageAfterIn', onPageIn);
          if (unmounted) return;
          // pageAfterIn 後に rAF で1フレーム待って確実にルーター状態を安定させる
          requestAnimationFrame(doNav);
        };
        f7.on('pageAfterIn', onPageIn);
      }
    };

    const init = async () => {
      // 既存サブスクの二重申込ガード
      // status API を単独で先に評価し、vehicle-statuses は best-effort で取得。
      // vehicle-statuses が失敗してもガード判定は status API だけで行う。
      if (authInfo?.m_customer_id) {
        try {
          const statusRes = await getSubscriptionStatusApi(
            authInfo.m_customer_id,
          );
          if (statusRes?.data?.success) {
            const sub = statusRes.data.data?.subscription;
            const pending = statusRes.data.data?.pending_subscription;

            // vehicle-statuses は best-effort（失敗してもガードは有効）
            let hasActiveSubElsewhere = false;
            try {
              const vehicleRes = await getSubscriptionVehicleStatusesApi(
                authInfo.m_customer_id,
              );
              const vehicleStates =
                vehicleRes?.data?.success && vehicleRes.data.data
                  ? vehicleRes.data.data.vehicle_states || []
                  : [];
              hasActiveSubElsewhere = vehicleStates.some(
                (vs) =>
                  vs.state === 'active' || vs.state === 'cancel_requested',
              );
            } catch {
              // vehicle-statuses 失敗時はゴーストフィルタ無効化
              // （安全側 = ガードを厳しくする）
            }

            const isActive = (s: typeof sub) =>
              !!s &&
              (s.apply_status === 3 ||
                (s.apply_status === 0 && s.status === 1));
            const isLinkedToOtherCar = (s: typeof sub) =>
              !!s && !!(s as any).t_stock_car_id;
            let guardPending =
              pending && !isActive(pending) && !isLinkedToOtherCar(pending)
                ? pending
                : null;
            let guardSub = sub && !isActive(sub) ? sub : null;
            // 別車両で契約済みサブスクが存在する場合、車両未紐付のレコードは
            // ゴーストレコードの可能性が高いためガード対象外
            // ただし apply_status=2（決済待ち）は実際の申込なので除外しない
            const isGhost = (s: typeof sub) =>
              !!s && !(s as any).t_stock_car_id && s.apply_status !== 2;
            if (hasActiveSubElsewhere) {
              if (guardPending && isGhost(guardPending)) {
                guardPending = null;
              }
              if (guardSub && isGhost(guardSub)) {
                guardSub = null;
              }
            }
            const existing = guardPending || guardSub;
            if (existing) {
              // 申込手続き中 or 決済待ちのサブスクが存在 → 適切な画面にリダイレクト
              if (existing.apply_status === 1 || existing.apply_status === 2) {
                // apply_status=1（初回決済待ち）/ 2（継続決済待ち）→ 支払い画面へ
                navigateWhenReady(paths.subscriptionPayment, {
                  reloadCurrent: true,
                  props: {
                    subscriptionId: existing.t_subscription_id,
                  },
                });
              } else {
                // apply_status=0, status=2（車検証登録待ち）→ 完了画面へ
                navigateWhenReady(paths.subscriptionComplete, {
                  reloadCurrent: true,
                  props: {
                    subscriptionId: existing.t_subscription_id,
                  },
                });
              }
              return;
            }
          }
        } catch {
          // status API 自体の失敗時は申込を許可（APIが最終ガード）
        }
      }

      await fetchPlan();
    };
    init();

    return () => {
      unmounted = true;
    };
  }, []);

  const fetchPlan = async () => {
    setPlanError(false);
    try {
      let res = await getSubscriptionPlansApi();
      if (!res?.data?.success) {
        console.warn('プラン取得: 1回目失敗、リトライ');
        res = await getSubscriptionPlansApi();
      }
      if (res?.data?.success && res?.data?.data?.plans?.[0]) {
        setPlan(res.data.data.plans[0]);
      } else {
        console.error('プラン取得失敗', res?.data);
        setPlanError(true);
      }
    } catch (e) {
      console.warn('プラン取得: 1回目例外、リトライ', e);
      try {
        const retry = await getSubscriptionPlansApi();
        if (retry?.data?.success && retry?.data?.data?.plans?.[0]) {
          setPlan(retry.data.data.plans[0]);
        } else {
          console.error('プラン取得失敗（リトライ後）', retry?.data);
          setPlanError(true);
        }
      } catch (e2) {
        console.error('プラン取得失敗（リトライ後例外）', e2);
        setPlanError(true);
      }
    }
  };

  // メーカー一覧取得
  useEffect(() => {
    if (carSelectMode !== 'manual') return;
    const fetchMakers = async () => {
      try {
        const res = await getSubscriptionMakersApi();
        if (res.data.success) {
          setMakers(res.data.data || []);
        }
      } catch (e) {
        console.error('メーカー取得エラー', e);
      }
    };
    if (makers.length === 0) fetchMakers();
  }, [carSelectMode]);

  // 車種一覧取得（メーカー選択時）
  useEffect(() => {
    if (!selectedMkCd) {
      setCarnames([]);
      return;
    }
    const fetchCarnames = async () => {
      try {
        const res = await getSubscriptionCarnamesApi(selectedMkCd);
        if (res.data.success) {
          setCarnames(res.data.data || []);
        }
      } catch (e) {
        console.error('車種取得エラー', e);
      }
    };
    fetchCarnames();
  }, [selectedMkCd]);

  const handleSubmit = async () => {
    if (isSubmitting || !plan) return;

    // 車両情報の確認
    const hasRegisteredCar = carSelectMode === 'registered' && selectedCar;
    const hasManualCar =
      carSelectMode === 'manual' && selectedMkCd && selectedCarnmCd;
    if (!hasRegisteredCar && !hasManualCar) return;

    setIsSubmitting(true);
    try {
      // テナント切り替え中は切り替え先法人として申し込み、元の所属法人を併せて記録する
      // 未切り替え時は m_affiliate_id に所属法人のみを送る（original_m_affiliate_id は付けない）
      const originalAffiliateId = store.state.customerInfo.m_affiliate_id;
      const targetAffiliateId =
        selectedTenant?.m_affiliate_id ?? originalAffiliateId;

      const params: any = {
        m_customer_id: authInfo.m_customer_id,
        m_subscription_plan_id: plan.id,
        m_affiliate_id: targetAffiliateId,
        ...(selectedTenant
          ? { original_m_affiliate_id: originalAffiliateId }
          : {}),
      };
      if (hasRegisteredCar) {
        params.t_stock_car_id = selectedCar!.t_stock_car_id;
      } else {
        params.mk_cd = selectedMkCd;
        params.carnm_cd = selectedCarnmCd;
      }
      if (isInheritanceApply && inheritanceStatus?.source_t_base_warranty_id) {
        params.inheritance_source_t_base_warranty_id =
          inheritanceStatus.source_t_base_warranty_id;
      }

      const res = await postSubscriptionApplyApi(params);

      if (res.data.success) {
        // 申込時点では契約未成立（status=2 停止 / apply_status=1）のため完了画面は挟まず、
        // そのまま決済へ進める。完了画面は activate 成功後にのみ表示する。
        f7.view.main.router.navigate(paths.subscriptionPayment, {
          props: {
            subscriptionId: res.data.data?.t_subscription_id,
            planName: plan.plan_name,
            monthlyFee:
              res.data.data?.monthly_billing_amount ??
              inheritanceStatus?.inherited_monthly_billing_amount ??
              plan.monthly_fee,
            hasRegisteredCar: !!hasRegisteredCar,
            selectedMakerName: hasManualCar ? selectedMakerName : undefined,
            selectedCarName: hasManualCar ? selectedCarName : undefined,
            tStockCarId: hasRegisteredCar
              ? selectedCar!.t_stock_car_id
              : undefined,
          },
        });
      } else {
        f7.dialog.alert(res.data.error_message || '申込に失敗しました');
      }
    } catch (e: any) {
      f7.dialog.alert(e.message || '申込処理中にエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickTerm = (termType: number) => {
    f7.view.main.router.navigate(paths.terms, {
      props: { termType },
    });
  };

  const isCarSelected =
    (carSelectMode === 'registered' && selectedCar) ||
    (carSelectMode === 'manual' && selectedMkCd && selectedCarnmCd);
  const displayMonthlyFee =
    inheritanceStatus?.inherited_monthly_billing_amount ?? plan?.monthly_fee;
  const isSameConditionInheritance =
    inheritanceStatus?.condition_type === 'same_as_warranty';

  return (
    <PageTemplate
      pageName="subscription-confirm"
      title="ご購入内容の確認"
      showNavBack
    >
      <div style={{ padding: '16px' }}>
        {/* プラン */}
        <div
          style={{
            padding: '20px',
            marginBottom: '16px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
          }}
        >
          <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
            プラン
          </div>
          <div
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '4px',
            }}
          >
            {plan?.plan_name ||
              (planError
                ? 'プラン情報を取得できませんでした'
                : '読み込み中...')}
          </div>
          {plan && (
            <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
              {displayMonthlyFee?.toLocaleString()}
              <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                円/月（税込）
              </span>
            </div>
          )}
          {planError && (
            <button
              onClick={fetchPlan}
              style={{
                marginTop: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                color: '#1E55E6',
                backgroundColor: '#fff',
                border: '1px solid #1E55E6',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              再読み込み
            </button>
          )}
        </div>

        {/* 車両選択 */}
        <div
          style={{
            padding: '20px',
            marginBottom: '16px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
          }}
        >
          <div
            style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}
          >
            対象の車両
          </div>

          {/* 車両固定表示（inheritance or store選択済み） */}
          {(() => {
            const fixedCar = isInheritanceApply ? inheritanceCar : initialCar;
            if (!fixedCar) return null;
            return (
              <div
                style={{
                  padding: '12px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  border: '2px solid #1E55E6',
                  backgroundColor: 'rgba(30,85,230,0.1)',
                }}
              >
                <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                  {fixedCar.maker_nm} {fixedCar.car_nm}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '2px',
                  }}
                >
                  {fixedCar.class_no} {fixedCar.hiragana} {fixedCar.registry_no}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#1E55E6',
                    marginTop: '4px',
                    fontWeight: 'bold',
                  }}
                >
                  {isInheritanceApply && isSameConditionInheritance
                    ? '既存保証と同一条件'
                    : 'サブスク申込対象'}
                </div>
              </div>
            );
          })()}

          {!isInheritanceApply && !initialCar && allCars.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <label
                onClick={() => {
                  setCarSelectMode('registered');
                  setSelectedMkCd(null);
                  setSelectedCarnmCd(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                <span
                  style={{
                    width: '23px',
                    height: '23px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {carSelectMode === 'registered' && (
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#1E55E6',
                      }}
                    />
                  )}
                </span>
                既に登録されている車から選ぶ
              </label>
              <label
                onClick={() => {
                  setCarSelectMode('manual');
                  setSelectedCar(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                <span
                  style={{
                    width: '23px',
                    height: '23px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {carSelectMode === 'manual' && (
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#1E55E6',
                      }}
                    />
                  )}
                </span>
                新しく車を登録する
              </label>
            </div>
          )}

          {/* 登録済み車両リスト */}
          {!isInheritanceApply &&
            !initialCar &&
            carSelectMode === 'registered' &&
            allCars.map((car) => {
              const isSelected =
                selectedCar?.t_stock_car_id === car.t_stock_car_id;
              return (
                <div
                  key={car.m_car_id || car.t_stock_car_id}
                  onClick={() => setSelectedCar(isSelected ? null : car)}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    borderRadius: '8px',
                    border: isSelected
                      ? '2px solid #1E55E6'
                      : '1px solid #c9c9c9',
                    backgroundColor: isSelected
                      ? 'rgba(30,85,230,0.1)'
                      : '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                    {car.maker_nm} {car.car_nm}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '2px',
                    }}
                  >
                    {car.class_no} {car.hiragana} {car.registry_no}
                  </div>
                </div>
              );
            })}

          {/* メーカー/車種セレクト */}
          {!isInheritanceApply && !initialCar && carSelectMode === 'manual' && (
            <div>
              <div style={{ marginBottom: '12px' }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                  }}
                >
                  メーカー{' '}
                  <span style={{ color: '#E65100', fontSize: '12px' }}>
                    必須
                  </span>
                </div>
                <select
                  value={selectedMkCd || ''}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setSelectedMkCd(val || null);
                    setSelectedCarnmCd(null);
                    setSelectedCarName('');
                    const maker = makers.find((m) => m.mk_cd === val);
                    setSelectedMakerName(maker?.mk_nm || '');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '15px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    backgroundColor: '#fff',
                    appearance: 'auto',
                  }}
                >
                  <option value="">選択してください</option>
                  {makers.map((m) => (
                    <option key={m.mk_cd} value={m.mk_cd}>
                      {m.mk_nm}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                  }}
                >
                  車種{' '}
                  <span style={{ color: '#E65100', fontSize: '12px' }}>
                    必須
                  </span>
                </div>
                <select
                  value={selectedCarnmCd || ''}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setSelectedCarnmCd(val || null);
                    const cn = carnames.find((c) => c.carnm_cd === val);
                    setSelectedCarName(cn?.car_nm || '');
                  }}
                  disabled={!selectedMkCd}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '15px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    backgroundColor: selectedMkCd ? '#fff' : '#f5f5f5',
                    appearance: 'auto',
                  }}
                >
                  <option value="">選択してください</option>
                  {carnames.map((c) => (
                    <option key={c.carnm_cd} value={c.carnm_cd}>
                      {c.car_nm}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* 規約同意 */}
        <div style={{ marginBottom: '24px' }}>
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '14px',
              padding: '20px 16px',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '12px',
              }}
            >
              サービス利用規約
            </div>
            <div
              style={{
                fontSize: '13px',
                color: '#666',
                lineHeight: 1.6,
                marginBottom: '16px',
              }}
            >
              お申し込みの前に、以下の利用規約を必ずご確認ください。
            </div>
            {plan?.term_type && (
              <div
                onClick={() => handleClickTerm(plan.term_type!)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 0',
                  borderTop: '1px solid #f0f0f0',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: '14px', color: '#1E55E6' }}>
                  サブスクリプションサービス利用規約
                </span>
                <i
                  className="icon-right"
                  style={{ fontSize: '12px', color: '#bbb' }}
                />
              </div>
            )}
            {plan?.coverages
              ?.filter((cov) => cov.term_type)
              .map((cov) => (
                <div
                  key={cov.id}
                  onClick={() => handleClickTerm(cov.term_type!)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 0',
                    borderTop: '1px solid #f0f0f0',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: '14px', color: '#1E55E6' }}>
                    {cov.coverage_name}利用規約
                  </span>
                  <i
                    className="icon-right"
                    style={{ fontSize: '12px', color: '#bbb' }}
                  />
                </div>
              ))}
          </div>
          <div
            onClick={() => setTermsAgreed(!termsAgreed)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                width: '22px',
                height: '22px',
                flexShrink: 0,
                borderRadius: '4px',
                border: termsAgreed ? 'none' : '2px solid #ccc',
                backgroundColor: termsAgreed ? '#1E55E6' : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {termsAgreed ? '✓' : ''}
            </div>
            <span>利用規約に同意する</span>
          </div>
        </div>

        {/* 申込ボタン */}
        <button
          onClick={handleSubmit}
          disabled={!isCarSelected || !termsAgreed || isSubmitting || !plan}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor:
              isCarSelected && termsAgreed && !isSubmitting
                ? '#1E55E6'
                : '#ccc',
            border: 'none',
            borderRadius: '28px',
            cursor:
              isCarSelected && termsAgreed && !isSubmitting
                ? 'pointer'
                : 'not-allowed',
          }}
        >
          {isSubmitting ? '処理中...' : '申し込む'}
        </button>
      </div>
    </PageTemplate>
  );
};
